import { SearchInput } from "@assetwatch/ui";
import type { ReactNode } from "react";

export interface SearchFilterToolbarProps {
  /** Accessible label for the search input. */
  searchAriaLabel: string;
  /** Current search field value. */
  searchValue: string;
  /** Placeholder text for the search input. */
  searchPlaceholder: string;
  /** Called on every keystroke. */
  onSearchChange: (value: string) => void;
  /** Called when the search should be committed (e.g. debounced submit). */
  onSearch?: (value: string) => void;
  /**
   * Filter control(s) rendered after the search input, e.g. a self-contained
   * `RequestFilters`/`ProcedureFilters` trigger-and-popover component.
   */
  children?: ReactNode;
}

/**
 * Bordered search-and-filters toolbar shared by Work list screens,
 * matching the POC's flat (no-shadow) Requests toolbar card.
 *
 * @ds-deviation: the DS `Tile` component always applies `shadow-elevation-1`
 * (hardcoded in packages/ui/src/primitives/tile/tile.tsx, not controllable
 * via props), so it cannot reproduce this toolbar's flat, bordered,
 * no-shadow look from the POC (requests.jsx line ~1029-1030: border +
 * background + radius, no box-shadow). This container is hand-built from DS
 * tokens instead (border-outline-subtle, bg-surface-container-lowest,
 * radius-md, spacing scale), matching the pattern already established by
 * ProceduresScreen's inline toolbar div. Proposed resolution: promote a
 * `shadow`/`elevation` prop onto `Tile` (default current behavior, opt out
 * for flat/bordered tiles) so this case doesn't need a hand-rolled div.
 */
function SearchFilterToolbar({
  searchAriaLabel,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  onSearch,
  children,
}: SearchFilterToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-(--spacing-8) rounded-(--radius-md) border border-outline-subtle bg-surface-container-lowest px-(--spacing-16) py-(--spacing-12)">
      <div className="min-w-0 max-w-60 flex-[1_1_7.5rem]">
        <SearchInput
          aria-label={searchAriaLabel}
          clearable
          onChange={onSearchChange}
          onSearch={onSearch}
          placeholder={searchPlaceholder}
          size="sm"
          value={searchValue}
        />
      </div>
      {children}
    </div>
  );
}

export { SearchFilterToolbar };
